import React, { useState } from 'react'
import { Image, ImageStyle } from 'react-native'
import { useNavigation } from '@react-navigation/native'
import { useSelector } from 'react-redux'
import { StackNavigationProp } from '@react-navigation/stack'
import { BottomSheet } from '@src/components'
import { Feature, useFeatureFlag } from '@src/components/FeatureFlag'
import { StyleService, useStyleSheet } from '@src/style/service'
import { Text } from '@src/components/base'
import { freeConsultationImage } from '@src/assets/images'
import { userSelector } from '@src/selectors/app'
import { AppStackParamList } from '@src/navigation/types'
import { UserPropertyKind } from '@src/types'
import { useDispatchAsync, useSnack } from '@src/utils'
import { ByosVideoCallsUpsellStep } from '../constants'
import { getNextByosVideoCallsUpsellStep, recordByosVideoCallsUpsellInteraction } from '../utils'

export const FreeNutritionistConsultationModal = () => {
  const styles = useStyleSheet(themedStyles)
  const navigation = useNavigation<StackNavigationProp<AppStackParamList>>()
  const dispatchAsync = useDispatchAsync()
  const showSnack = useSnack()
  const notificationInboxEnabled = useFeatureFlag(Feature.NotificationInbox)
  const user = useSelector(userSelector)
  const [isUpdatingStep, setIsUpdatingStep] = useState(false)

  const updateStep = async () => {
    if (!user) {
      return
    }

    // Inbox OFF: this campaign is fully client-driven and persisted in local storage.
    if (!notificationInboxEnabled) {
      recordByosVideoCallsUpsellInteraction(ByosVideoCallsUpsellStep.FreeConsultationModal, user.id)
      return
    }

    const nextStep = getNextByosVideoCallsUpsellStep(ByosVideoCallsUpsellStep.FreeConsultationModal)

    if (!nextStep) {
      return
    }

    try {
      // Inbox ON: backend campaign orchestrator uses this user property as the source of truth.
      // Updating the property to the next step can trigger downstream scheduling/cancellation of inbox notifications.
      await dispatchAsync({
        type: 'users/upsertUserProperty',
        payload: {
          kind: UserPropertyKind.ByosVideoCallsUpsell,
          value: nextStep,
        },
      })
      // Refresh the user so modal visibility and local campaign state immediately reflect backend changes.
      await dispatchAsync({ type: 'users/fetch' })
    } catch {
      showSnack('Failed to update your campaign status.', null, 'error')
      throw new Error('failed_to_update_byos_video_calls_upsell_step')
    }
  }

  const onClaimNowPress = async () => {
    if (isUpdatingStep) {
      return
    }

    setIsUpdatingStep(true)

    try {
      await updateStep()
    } catch {
      // continue navigation when update fails so user is not blocked
    } finally {
      setIsUpdatingStep(false)
    }

    navigation.replace('InsuranceQuestionnaire', {
      skipIntroScreen: true,
    })
  }

  const onDismiss = async () => {
    if (isUpdatingStep) {
      return
    }

    if (user) {
      setIsUpdatingStep(true)

      try {
        await updateStep()
      } catch {
        // no-op
      } finally {
        setIsUpdatingStep(false)
      }
    }
  }

  return (
    <BottomSheet
      primaryButton={{
        text: 'Claim now',
        onPress: onClaimNowPress,
        disabled: isUpdatingStep,
      }}
      showDismissButton
      onDismiss={onDismiss}
      dismissButtonText="No thanks"
      buttonDirection="column-reverse"
    >
      <Image source={freeConsultationImage.imageSource} style={styles.image as ImageStyle} />
      <Text style={styles.title} type="title-2">
        Claim Your FREE Expert Nutritionist Consultation Today
      </Text>
      <Text style={styles.content} type="regular">
        Transform your CGM data into real insights with a one-on-one expert consultation. Ask a
        credentialed nutritionist questions about your glucose patterns, diet choices, and health
        concerns—at no cost to you.
      </Text>
    </BottomSheet>
  )
}

const themedStyles = StyleService.create({
  title: {
    textAlign: 'center',
    marginVertical: 16,
  },
  content: {
    textAlign: 'center',
    marginBottom: 16,
  },
  image: {
    aspectRatio: 3 / 2,
    width: '100%',
    height: undefined,
    borderRadius: 20,
  },
})
